import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, X, Loader2, Globe, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { RecommendationCard } from '../components/RecommendationCard';
import { CategoryChips } from '../components/CategoryChips';

export const Explore = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Explore searches Explore: the term and category narrow the wider-network
  // feed only. Person filtering stays on the trusted Feed.
  const load = useCallback(async (q, cat) => {
    try {
      setLoading(true);
      const data = await api.getExplore({ q: q.trim(), category: cat });
      setItems(data.items || []);
      setCursor(data.nextCursor || null);
      setError(null);
    } catch (err) {
      setError('We could not load Explore just now.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const t = setTimeout(() => load(query, category), query ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, query, category]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await api.getExplore({ cursor, q: query.trim(), category });
      setItems((prev) => [...prev, ...(data.items || [])]);
      setCursor(data.nextCursor || null);
    } catch (err) {
      setError('We could not load more just now.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSaveChange = (recId, isSaved) => {
    setItems((prev) => prev.map((r) => (r.id === recId ? { ...r, isSaved } : r)));
  };

  const dropItem = (recId) => setItems((prev) => prev.filter((r) => r.id !== recId));

  if (authLoading) {
    return (
      <div className="page" data-testid="explore-page">
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="page" data-testid="explore-page">
        <div className="placeholder">
          <h2 className="placeholder-title">Explore is for members</h2>
          <button className="placeholder-btn" onClick={() => navigate('/signin')} data-testid="explore-signin">
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page" data-testid="explore-page">
      <header className="feed-header">
        <button className="back-btn inline" onClick={() => navigate('/')} aria-label="Back to Feed" data-testid="explore-back">
          <ArrowLeft />
        </button>
        <h1 className="feed-greeting">Explore</h1>
        <p className="feed-sub">
          Recommendations that members chose to share with everyone on irecommend.
        </p>
      </header>

      <div className="filter-bar">
        <div className="search-field">
          <SearchIcon className="search-field-icon" />
          <input
            type="text"
            className="search-field-input"
            placeholder="Search Explore…"
            aria-label="Search Explore"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="explore-search"
          />
          {query && (
            <button
              className="search-field-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              data-testid="explore-search-clear"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <CategoryChips value={category} onChange={setCategory} idPrefix="explore" />
      </div>

      {loading ? (
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      ) : error ? (
        <div className="placeholder" data-testid="explore-error">
          <p className="placeholder-text">{error}</p>
          <button className="placeholder-btn" onClick={() => load(query, category)}>Try again</button>
        </div>
      ) : items.length > 0 ? (
        <>
          <div className="feed-list" data-testid="explore-list">
            {items.map((rec) => (
              <RecommendationCard
                key={rec.id}
                recommendation={rec}
                onSaveChange={handleSaveChange}
                onDeleted={dropItem}
                onReported={dropItem}
              />
            ))}
          </div>
          {cursor && (
            <div className="explore-more">
              <button
                className="placeholder-btn"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="explore-load-more"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="placeholder" data-testid="explore-empty">
          <Globe className="placeholder-icon" />
          <h2 className="placeholder-title">Nothing here yet</h2>
          <p className="placeholder-text">
            {query || category
              ? 'Try a different word or another category.'
              : 'When members share a recommendation with everyone, it will show up here.'}
          </p>
        </div>
      )}
    </div>
  );
};
